import { useRef, useEffect, useState, useMemo } from 'react';

const useFileUpload = ({
  override = false,
  limit,
  initialFile,
  onFileExceedLimit,
  onFileSizeExceedLimit,
  maximumSize = Number.MAX_SAFE_INTEGER,
}) => {
  const [files, setFiles] = useState(initialFile);

  const inputRef = useRef();

  useEffect(() => {
    const inputEL = inputRef?.current;

    const handleSetFiles = ({ newFiles, isMultiple, override }) => {
      setFiles((prev) => {
        // Check file limit
        const willUpdatedFilesLength = newFiles?.length ?? 0;
        const currentFilesLength = prev?.length ?? 0;
        const currentFileCount = override
          ? willUpdatedFilesLength
          : willUpdatedFilesLength + currentFilesLength;

        if (limit && currentFileCount > limit) {
          typeof onFileExceedLimit === 'function' && onFileExceedLimit();
          const updatingFiles = [...(prev ?? [])];
          return updatingFiles;
        }
        let updatingFiles = [];
        if (isMultiple) updatingFiles = override ? newFiles : [...(prev ?? []), ...newFiles];
        else updatingFiles = override ? newFiles[0] : [...(prev ?? []), newFiles[0]];

        return updatingFiles;
      });
    };

    // Handle onChange event
    const onChange = async (e) => {
      const parsedFiles = [];
      const target = e.target;

      // Loop through files
      for (const fileIndex in target.files) {
        // check if index is a number
        if (isNaN(fileIndex)) continue;

        // get file object
        const file = target.files[fileIndex];

        // select properties
        const parsedFile = {
          source: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          file: file, // original file object
        };

        // add to parsed file list
        parsedFiles.push(parsedFile);
      }

      const hasSomeFileSizeExceedLimit = parsedFiles.some(({ size }) => size > maximumSize);
      if (hasSomeFileSizeExceedLimit) {
        typeof onFileSizeExceedLimit === 'function' && onFileSizeExceedLimit();
        return;
      }
      // update files state hook
      handleSetFiles({ newFiles: parsedFiles, isMultiple: target.multiple, override });
      return;
    };

    // add event listener
    inputEL.addEventListener?.('change', onChange);

    return () => {
      inputEL.removeEventListener?.('change', onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override]);

  // Handle upload
  const uploadFile = (e) => {
    e?.preventDefault();
    if (!inputRef || !inputRef.current) console.error('input element not found');
    inputRef.current.value = null;
    inputRef?.current?.click?.();
  };

  const removeFile = (removeIndex) => {
    const currentFiles = [...files];
    const updatedFiles = currentFiles.filter((e, index) => removeIndex !== index);
    setFiles(updatedFiles);
  };

  const clearFile = () => {
    setFiles(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => [inputRef, { files, uploadFile, removeFile, clearFile }], [files]);
};

export default useFileUpload;
